import React from "react";
import { SearchBar } from "@/components/common/SearchBar";
import { SectionRule } from "components/common/SectionRule";
import { MetaAddButton } from "@/components/main/datasets/DatasetMetaSection";
import type { RowContextMenu } from "@/components/hooks/useContextMenu";

interface ListEmptyShellProps {
  /// Section icon — reused in the label row and (dimmed) in the hint line.
  icon: React.ReactNode;
  /// Section label, e.g. "Datasets".
  label: string;
  /// First-run hint, e.g. "Create a dataset to group related tables."
  hint: string;
  /// Zero-count footer text, e.g. "0 datasets".
  countLabel: string;
  /// Omit both to render a count-only footer (e.g. Trash has no add action).
  addLabel?: string;
  onAdd?: () => void;
  addDisabled?: boolean;
  /// Row-menu trigger props for the add pill, so the empty state offers the same
  /// create variants as the populated list's footer pill.
  addMenuTriggerProps?: RowContextMenu["triggerProps"];
  /// Omit to render no filter bar (e.g. tokens).
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
  };
}

/// The zero-items view for the Overview-family list pages (Datasets, Teams,
/// Connections, Access tokens). Renders the SAME shell as the populated list —
/// fixed filter bar, sticky section header, footer count + add pill — so the
/// layout never jumps once the first item lands. The section body is a single
/// muted "…will appear here" hint and the add pill pulses to point the way.
export const ListEmptyShell: React.FC<ListEmptyShellProps> = ({
  icon,
  label,
  hint,
  countLabel,
  addLabel,
  onAdd,
  addDisabled,
  addMenuTriggerProps,
  search,
}) => (
  <>
    {search && (
      <div className="shrink-0 mb-6">
        <SearchBar
          value={search.value}
          onChange={search.onChange}
          placeholder={search.placeholder}
          size="lg"
        />
      </div>
    )}

    {/* Only the list scrolls; the footer below stays pinned and its pulse
        ring is never clipped by this container's overflow. */}
    <div className="min-h-0 overflow-y-auto">
      <div className="mb-8">
        <div className="sticky top-0 z-10 bg-bg overflow-hidden">
          <SectionRule inset icon={icon} label={label} />
        </div>
        <div className="flex items-center gap-2 item-height px-1 text-small text-fg-muted italic">
          {/* Empty w-5 spacer aligns the hint with the label text, not its
              icon — matches the Overview Recent/Pinned sections. */}
          <span className="w-5 shrink-0" />
          <span>{hint}</span>
        </div>
      </div>
    </div>

    <div className="shrink-0 flex items-center mt-6">
      <span
        className="font-mono text-fg-muted"
        style={{
          fontSize: "var(--font-size-x-small)",
          letterSpacing: "0.04em",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {countLabel}
      </span>
      {onAdd && addLabel && (
        <span className="ml-auto pulse-hint">
          <MetaAddButton
            label={addLabel}
            onClick={onAdd}
            disabled={addDisabled}
            menuTriggerProps={addMenuTriggerProps}
          />
        </span>
      )}
    </div>
  </>
);
